import React from 'react';
import { motion } from 'framer-motion';
import { Project } from '../../types';
import { ExternalLink, Github, Smartphone, Youtube } from 'lucide-react';
import ImageSlideshow from '../ui/ImageSlideshow';
import YouTubeEmbed from '../ui/YouTubeEmbed';

interface WorkCardProps {
    project: Project;
    index: number;
}

const WorkCard: React.FC<WorkCardProps> = ({ project, index }) => {
    const hasSlideshow = project.images && project.images.length > 0;
    const hasYoutube = project.youtube;

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="group relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300"
        >
            {/* Media Container */}
            <div className="relative h-64 md:h-80 overflow-hidden bg-gradient-to-br from-sky-100 to-sky-200">
                {hasYoutube && project.youtube ? (
                    <YouTubeEmbed url={project.youtube} title={project.title} />
                ) : hasSlideshow && project.images ? (
                    <ImageSlideshow images={project.images} />
                ) : project.image ? (
                    <>
                        <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <span className="text-lg">No preview available</span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
                <div>
                    <h3 className="text-2xl font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
                        {project.titleJp || project.title}
                    </h3>
                    {project.titleJp && (
                        <p className="text-sm text-slate-500 mt-1">{project.title}</p>
                    )}
                </div>

                <p className="text-slate-600 leading-relaxed jp-text whitespace-pre-line">
                    {project.descriptionJp || project.description}
                </p>
                {/* Technologies */}
                <div className="flex flex-wrap gap-2 pt-2">
                    {project.technologies.map((tech) => (
                        <span
                            key={tech}
                            className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded"
                        >
              {tech}
            </span>
                    ))}
                </div>

                {/* Links */}
                <div className="flex items-center gap-4 pt-4">
                    {project.github && (
                        <motion.a
                            href={project.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
                        >
                            <Github size={18} />
                            <span className="text-sm font-medium">GitHub</span>
                        </motion.a>
                    )}
                    {project.appStore && (
                        <motion.a
                            href={project.appStore}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
                        >
                            <Smartphone size={18} />
                            <span className="text-sm font-medium">App Store</span>
                        </motion.a>
                    )}
                    {project.youtube && (
                        <motion.a
                            href={project.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
                        >
                            <Youtube size={18} />
                            <span className="text-sm font-medium">YouTube</span>
                        </motion.a>
                    )}
                    {project.link && (
                        <motion.a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-slate-600 hover:text-sky-600 transition-colors"
                        >
                            <ExternalLink size={18} />
                            <span className="text-sm font-medium">View Project</span>
                        </motion.a>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default WorkCard;